import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  Autocomplete,
  Radio,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  RadioGroup,
  Stack,
  TextField,
  Divider
} from '@mui/material'

import {
  Close as CloseIcon
} from '@mui/icons-material'

import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'

import { defaultFunction, getVisitDestination } from '../../utilities/utilities'
import { api } from '../../../api/api'
import { defaultCatch, defaultSuccess, setSnackbar, setLoading } from '../../../state/actions'
import db, { enqueueVisitPhoto } from '../../../db/db'
import { runPoll } from '../../../sync/SyncEngine'
import Field from '../Field/Field'
import VisitAutoComplete from '../VisitAutoComplete/VisitAutoComplete'
import { DEFAULT_AUTOCOMPLETE, MAX_LICENSE_PLATE_LENGTH, REGEX_LICENSE_PLATE } from '../../../variables'
import IDDialog from '../IDDialog/IDDialog'
import IdentificationPhotoButton from '../IdentificationPhotoButton/IdentificationPhotoButton'
import { findActiveVisitByCardNumber } from '../../utilities/visitValidations'

function EditDialog(props) {
  const {
    open = false,
    visitDestinations = [],
    enabled_visit_photo = false,
    fetchVisits = defaultFunction,
    defaultCatch,
    defaultSuccess,
    setLoading,
    setSnackbar,
    item = {},
    ask_visit_identification = false,
    custom_visit_identification_label = '',
    ask_visit_comes_from = false,
    ask_visit_laptop = false,
    ask_visit_card_number = false,
    ask_visit_license_plate = false,
    save_identification_photo = false,
    isOnline = true,
  } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const identificationLabel = custom_visit_identification_label || t('Identification')


  const [identification, setIdentification] = useState(item.identification)
  const [first_name, setFirstName] = useState('')
  const [last_name_one, setLastNameOne] = useState('')
  const [last_name_two, setLastNameTwo] = useState('')
  const [comes_from, setComesFrom] = useState('')
  const [visitDestination, setVisitDestination] = useState(DEFAULT_AUTOCOMPLETE)
  const [hasCardNumber, setHasCardNumber] = useState(false)
  const [has_laptop, setHasLaptop] = useState(false)
  const [card_number, setCardNumber] = useState('')
  const [cardNumberError, setCardNumberError] = useState('')
  const [isCheckingCardNumber, setIsCheckingCardNumber] = useState(false)
  const cardNumberRequestIdRef = useRef(0)
  const [laptop_serie, setLaptopSerie] = useState('')
  const [notes, setNotes] = useState('')
  const [license_plate, setLicensePlate] = useState('')
  const [licensePlateError, setLicensePlateError] = useState('')
  const [photo, setPhoto] = useState('')
  const [openIDDialog, setOpenIDDialog] = useState(false)
  const [identificationPhoto, setIdentificationPhoto] = useState('')
  const [isLoadingIdentificationPhoto, setIsLoadingIdentificationPhoto] = useState(false)
  const photoUrlRef = useRef('')
  const identificationPhotoUrlRef = useRef('')


  const onClose = useCallback(() => {
    if (photoUrlRef.current) {
      URL.revokeObjectURL(photoUrlRef.current)
      photoUrlRef.current = ''
    }
    if (identificationPhotoUrlRef.current) {
      URL.revokeObjectURL(identificationPhotoUrlRef.current)
      identificationPhotoUrlRef.current = ''
    }
    setPhoto('')
    setIdentificationPhoto('')
    setIsLoadingIdentificationPhoto(false)
    if (props.onClose) props.onClose()
  }, [props])

  const evalLicensePlate = useCallback((value = license_plate) => {
    const error = value && !REGEX_LICENSE_PLATE.test(value)
      ? t('invalid license plate')
      : ''
    setLicensePlateError(error)
    return error
  }, [license_plate, t])

  const setVisit = useCallback(visit => {
    const {
      identification = '',
      first_name = '',
      last_name_one = '',
      last_name_two = '',
      destination = DEFAULT_AUTOCOMPLETE,
      comes_from = '',
      laptop_serie = '',
      has_laptop = false,
      card_number = '',
      notes = '',
      license_plate: visitLicensePlate = ''
    } = visit

    setIdentification(identification)
    setFirstName(first_name)
    setLastNameOne(last_name_one)
    setLastNameTwo(last_name_two)
    setComesFrom(comes_from)
    setNotes(notes)
    const plate = String(visitLicensePlate || '').toUpperCase()
    setLicensePlate(plate)
    evalLicensePlate(plate)

    setVisitDestination(destination)

    if (has_laptop && laptop_serie) {
      setHasLaptop(true)
      setLaptopSerie(laptop_serie)
    } else {
      setHasLaptop(false)
      setLaptopSerie('')
    }

    if (card_number) {
      setHasCardNumber(true)
      setCardNumber(card_number)
      setCardNumberError('')
    } else {
      setHasCardNumber(false)
      setCardNumber('')
      setCardNumberError('')
    }


  }, [evalLicensePlate])

  useEffect(() => {
    if (!open) return

    let isMounted = true
    setVisit(item)

    if (photoUrlRef.current) {
      URL.revokeObjectURL(photoUrlRef.current)
      photoUrlRef.current = ''
    }
    if (identificationPhotoUrlRef.current) {
      URL.revokeObjectURL(identificationPhotoUrlRef.current)
      identificationPhotoUrlRef.current = ''
    }
    setPhoto('')
    setIdentificationPhoto('')
    setIsLoadingIdentificationPhoto(false)

    const { photo = {}, credential_photo = {} } = item

    if (photo.id) {
      api.get(`visit/photo/${photo.id}`, { responseType: "blob" })
        .then(defaultSuccess)
        .then(({ data }) => {
          if (!isMounted || !data?.size) return
          photoUrlRef.current = URL.createObjectURL(data)
          setPhoto(photoUrlRef.current)
        })
        .catch(error => {
          if (!isMounted) return
          defaultCatch(error, navigate)
        })
    }

    if (credential_photo.id) {
      setIsLoadingIdentificationPhoto(true)
      api.get(`visit/photo/${credential_photo.id}`, { responseType: "blob" })
        .then(defaultSuccess)
        .then(({ data }) => {
          if (!isMounted || !data?.size) return
          identificationPhotoUrlRef.current = URL.createObjectURL(data)
          setIdentificationPhoto(identificationPhotoUrlRef.current)
        })
        .catch(error => {
          if (!isMounted) return
          defaultCatch(error, navigate)
        })
        .finally(() => {
          if (!isMounted) return
          setIsLoadingIdentificationPhoto(false)
        })
    }

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item])

  useEffect(() => {
    return () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current)
      if (identificationPhotoUrlRef.current) URL.revokeObjectURL(identificationPhotoUrlRef.current)
    }
  }, [])

  const onUpdateVisit = useCallback(async () => {
    const uuid = item.uuid
    if (!uuid) return null

    const destination = visitDestinations.find(d => d.id === visitDestination.id) || visitDestination

    // Track exactly which fields changed so the push only sends those to the
    // server (avoids overwriting server-side fields that weren't touched).
    const changedFields = {
      identification,
      first_name,
      last_name_one,
      last_name_two,
      destination_id: visitDestination.id,
      comes_from,
      card_number,
      notes,
      has_laptop,
      laptop_serie,
      license_plate,
    }

    // Edicion online directa (igual que `master`), solo posible si la visita
    // ya tiene `id` real del servidor. Si sigue `pending` de una creacion
    // offline que todavia no sincronizo, no hay endpoint al que hacer PUT
    // todavia — se cae al flujo de cola local de siempre, que de todas
    // formas se resuelve en cuanto ese create sincronice.
    if (isOnline && item.id) {
      try {
        const response = await api.put(`/visit/${item.id}`, { visit: changedFields })
        defaultSuccess(response)
        const { data } = response

        if (data?.success !== false) {
          const updatedVisit = {
            ...item,
            ...changedFields,
            ...data.visit,
            destination, // objeto completo para el listado local; el backend solo devuelve {id, name}
            uuid,
            syncStatus: 'synced',
            syncError: null,
            pushAttempts: 0,
          }
          await db.visits.put(updatedVisit)
          window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
          return { id: item.id, uuid }
        }

        setSnackbar({
          open: true,
          message: data?.errors?.[0] || t('Error saving visit'),
          severity: 'error',
          autoHideDuration: 2000,
          vertical: 'top',
          horizontal: 'right',
        })
        return null
      } catch (error) {
        // Falla de red, no de validacion: se degrada al flujo `pending` de
        // abajo para que `SyncEngine` reintente el mismo cambio.
        console.warn('[EditDialog] Error actualizando visita online, se encola para reintento:', error)
      }
    }

    const updates = {
      ...changedFields,
      destination,        // kept in Dexie for display; not sent to API directly
      syncStatus: 'pending',
      pushAttempts: 0,
      _pendingChanges: changedFields,
    }

    await db.visits.update(uuid, updates)
    window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
    if (isOnline) {
      // `runPoll`: sube la edición sin un `runSync` completo.
      runPoll()
        .catch(console.error)
    }
    return { id: item.id, uuid }
  }, [identification, first_name, last_name_one, last_name_two, visitDestination, visitDestinations, comes_from, card_number, notes, has_laptop, laptop_serie, license_plate, item, isOnline, defaultSuccess, setSnackbar, t])

  const validateActiveCardNumber = useCallback(async () => {
    const normalizedCardNumber = (card_number || '').trim()

    // Guarda "gana el último": `card_number` cambia en cada tecla y dispara
    // esta validación tanto desde el useEffect como desde onBlur, por lo que
    // puede haber varias consultas en vuelo a la vez. Sin esto, una respuesta
    // más lenta de un valor VIEJO puede resolver después de la del valor
    // NUEVO y pisar el estado de error con un resultado obsoleto.
    const requestId = ++cardNumberRequestIdRef.current
    const isStale = () => requestId !== cardNumberRequestIdRef.current

    if (!ask_visit_card_number || !hasCardNumber || !normalizedCardNumber) {
      if (!isStale()) setCardNumberError('')
      return false
    }

    setIsCheckingCardNumber(true)
    try {
      const activeVisit = await findActiveVisitByCardNumber({
        cardNumber: normalizedCardNumber,
        excludeVisitId: item.id,
        excludeVisitUuid: item.uuid,
      })

      if (isStale()) return false
      const hasConflict = !!activeVisit
      setCardNumberError(hasConflict ? t('card_number_in_use') : '')
      return hasConflict
    } catch (error) {
      console.error('[EditDialog] Error validating card number:', error)
      if (!isStale()) setCardNumberError('')
      return false
    } finally {
      if (!isStale()) setIsCheckingCardNumber(false)
    }
  }, [ask_visit_card_number, card_number, hasCardNumber, item.id, item.uuid, t])

  useEffect(() => {
    if (!ask_visit_card_number || !hasCardNumber || !card_number.trim()) return
    validateActiveCardNumber()
  }, [ask_visit_card_number, card_number, hasCardNumber, validateActiveCardNumber])

  const onChangeLicensePlate = useCallback(event => {
    const value = event.target.value || ''
    if (value.length > MAX_LICENSE_PLATE_LENGTH) return
    setLicensePlate(value.toUpperCase())
    setLicensePlateError('')
  }, [])

  const onUpdate = useCallback(async () => {
    setLoading(true)
    const visit = await onUpdateVisit()
    if (!visit) {
      setLoading(false)
      return
    }
    // Se encola en vez de subirse aquí: `item.id` es null mientras la visita
    // siga sin sincronizar, y el endpoint de foto exige el id del servidor.
    // `SyncEngine.pushPendingPhotos` la sube en cuanto ese id exista.
    if (save_identification_photo && identificationPhoto) {
      await enqueueVisitPhoto(visit.uuid, 'credential', identificationPhoto)
    }
    setLoading(false)
    setSnackbar({
      open: true,
      message: t('Successfully registered visitor'),
      severity: 'success',
      autoHideDuration: 2000,
      vertical: 'top',
      horizontal: 'right',
    })
    onClose()
    fetchVisits()
  }, [setLoading, onUpdateVisit, identificationPhoto, t, onClose, fetchVisits, setSnackbar, save_identification_photo])

  const onChangeCardNumber = value => {
    setHasCardNumber(value === 'true')
    setCardNumber('')
    setCardNumberError('')
  }

  const onChangePortail = value => {
    setHasLaptop(value === 'true')
    setLaptopSerie('')
  }

  const onChangeVisitDestination = (_, visitDestination) => {
    if (!visitDestination) setVisitDestination(DEFAULT_AUTOCOMPLETE)
    else setVisitDestination(visitDestination)
  }

  const isEnableSave = useCallback(() => {
    return (
      first_name &&
      last_name_one &&
      visitDestination.id !== -1 &&
      (!ask_visit_comes_from || comes_from) &&
      (!ask_visit_card_number || !hasCardNumber || card_number) &&
      (!ask_visit_laptop || !has_laptop || laptop_serie) &&
      (!ask_visit_license_plate || (license_plate && REGEX_LICENSE_PLATE.test(license_plate))) &&
      !licensePlateError &&
      !cardNumberError &&
      !isCheckingCardNumber
    )
  }, [ask_visit_card_number, ask_visit_comes_from, ask_visit_laptop, ask_visit_license_plate, cardNumberError, card_number, comes_from, first_name, hasCardNumber, has_laptop, isCheckingCardNumber, laptop_serie, last_name_one, licensePlateError, license_plate, visitDestination])


  return (
    <Dialog
      className='EditDialog'
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title">
      <DialogContent className="dialog-info-content">
        <Stack gap={4} alignItems="left">
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center" gap={1} width="100%">
              <IconButton
                aria-label="close"
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
              <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between" width="100%">
                <label>
                  {t("Edit the visitor record")}
                </label>
                {save_identification_photo &&
                  <IdentificationPhotoButton
                    onClick={() => setOpenIDDialog(true)}
                    identificationPhoto={identificationPhoto}
                    saveIdentificationPhoto={save_identification_photo}
                    loading={isLoadingIdentificationPhoto}
                  />
                }
              </Stack>
            </Stack>
          </Stack>
          {ask_visit_identification &&
            <Field
              direction="column"
              alignItems="left"
              label={identificationLabel}
              value={identification}
              onChange={e => setIdentification(e.target.value)}
            />
          }
          <Stack direction="column" gap={2}>
            <label>{t("Enter the visitor details")}</label>
            <Stack direction="row" gap={2}>
              <Field
                sx={{ width: '100%' }}
                placeholder={t('name')}
                value={first_name}
                onChange={e => setFirstName(e.target.value)}
              />
              <Field
                sx={{ width: '100%' }}
                placeholder={`${t('last name')} 1`}
                value={last_name_one}
                onChange={e => setLastNameOne(e.target.value)}
              />
              <Field
                sx={{ width: '100%' }}
                placeholder={`${t('last name')} 2`}
                value={last_name_two}
                onChange={e => setLastNameTwo(e.target.value)}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={2}>
            {
              ask_visit_comes_from &&
              <VisitAutoComplete
                sx={{ width: '100%' }}
                direction="column"
                alignItems="left"
                label={t('comes from')}
                value={comes_from}
                setValue={setComesFrom}
              />
            }
            <Stack
              sx={{ width: '100%' }}
              className='Field'
              gap={2}
              direction="column"
              alignItems="left"
              justifyContent="space-between"
            >
              <label>{t('go to')}</label>
              <Autocomplete
                size='small'
                fullWidth
                options={getVisitDestination(visitDestinations)}
                getOptionLabel={option => option.name}
                onChange={onChangeVisitDestination}
                value={visitDestination}
                renderInput={(params) => {
                  const { key, ...restParams } = params;
                  return <TextField
                    {...restParams}
                    label={t('write')}
                    variant="filled"
                  />
                }}
              />
            </Stack>
          </Stack>
          {ask_visit_license_plate &&
            <Field
              direction="column"
              alignItems="left"
              label={t('license_plate')}
              value={license_plate}
              onChange={onChangeLicensePlate}
              onBlur={() => evalLicensePlate()}
              error={!!licensePlateError}
              helperText={licensePlateError}
            />
          }
          {(ask_visit_card_number || ask_visit_laptop) &&
            <Stack
              direction="row"
              gap={2}
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
            >
              {ask_visit_card_number &&
                <Stack
                  className='Field'
                  gap={2}
                  direction="column"
                  alignItems="left"
                  sx={{ width: '100%' }}
                >
                  <label>{t('Card')}</label>
                  <Stack
                    gap={2}
                    direction="column"
                  >
                    <FormControl>
                      <RadioGroup
                        row
                        name="card"
                        value={hasCardNumber}
                        onChange={(_, value) => onChangeCardNumber(value)}
                      >
                        <FormControlLabel value={true} control={<Radio />} label={t('Yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('No')} />
                      </RadioGroup>
                    </FormControl>
                    {hasCardNumber &&
                      <Field
                        fullWidth
                        label={t('Card number')}
                        value={card_number}
                        onChange={e => {
                          setCardNumber(e.target.value)
                          setCardNumberError('')
                        }}
                        onBlur={validateActiveCardNumber}
                        error={!!cardNumberError}
                        helperText={cardNumberError}
                        alignItems={!!cardNumberError ? 'start' : 'center'}
                      />
                    }
                  </Stack>
                </Stack>
              }
              {ask_visit_laptop &&
                <Stack
                  className='Field'
                  gap={2}
                  direction="column"
                  alignItems="left"
                  sx={{ width: '100%' }}
                >
                  <label>{t("Laptop")}</label>
                  <Stack
                    gap={2}
                    direction="column"
                  >
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby="laptop-radio"
                        name="laptop"
                        value={has_laptop}
                        onChange={(_, value) => onChangePortail(value)}
                      >
                        <FormControlLabel value={true} control={<Radio />} label={t('Yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('No')} />
                      </RadioGroup>
                    </FormControl>
                    {has_laptop &&
                      <Field
                        fullWidth
                        label={t('No. Serie')}
                        value={laptop_serie}
                        onChange={e => setLaptopSerie(e.target.value)}
                      />
                    }
                  </Stack>
                </Stack>
              }
            </Stack>

          }


          {enabled_visit_photo &&
            photo &&
            <Stack
              className='Field'
              gap={2}
              direction="column"
              alignItems="left"
            >
              <label>{t('Photo (optional)')}</label>
              <div className='photo-prev-wrapper'>
                <img src={photo} alt={t("user")} />
              </div>

            </Stack>
          }
          <Field
            direction='column'
            alignItems='left'
            minRows={4}
            multiline
            label={` ${t('Notes')} ${t('(optional)')}`}
            value={notes}
            placeholder={t("If you wish, you can write a note.")}
            onChange={e => setNotes(e.target.value)}
          />

          <Stack
            alignItems="end"
          >
            <Button
              sx={{ width: 316 }}
              disabled={!isEnableSave()}
              variant="contained"
              size="large"
              onClick={onUpdate}
            >
              {t('confirm')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <IDDialog
        open={openIDDialog}
        onClose={() => setOpenIDDialog(false)}
        setPhoto={setIdentificationPhoto}
        photo={identificationPhoto}
        enabled_OCR_extraction={false}
      />
    </Dialog>
  )
}

const mapStateToProps = state => {
  return {
    visitDestinations: state.profile.visitDestinations,
    enabled_visit_photo: state.profile.enabled_visit_photo,
    enabled_visit_qr: state.profile.enabled_visit_qr,
    ask_visit_identification: state.profile.ask_visit_identification,
    custom_visit_identification_label: state.profile.custom_visit_identification_label,
    ask_visit_comes_from: state.profile.ask_visit_comes_from,
    ask_visit_laptop: state.profile.ask_visit_laptop,
    ask_visit_card_number: state.profile.ask_visit_card_number,
    ask_visit_license_plate: state.profile.ask_visit_license_plate,
    // Sin gating por `isOnline`: la foto se encola en Dexie y `SyncEngine` la
    // sube al reconectar (ver `enqueueVisitPhoto`).
    save_identification_photo: state.profile.save_identification_photo,
    isOnline: state.utilities.isOnline,
  }
}

const mapDispatchToProps = {
  setSnackbar,
  setLoading,
  defaultSuccess,
  defaultCatch
}

export default connect(mapStateToProps, mapDispatchToProps)(EditDialog)
